'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box } from '@chakra-ui/react'
import { Location, ProductOpeningHoursResponse, Region } from '@leid/api-locations'
import { Manager } from '@leid/api-managers'
import { ProductAvailabilityInfo } from '@leid/api-orders'
import { usePathname, useRouter } from 'next/navigation'

import { GoBackLink } from 'app/components/shared/nav/GoBackLink'
import { CustomerInfo } from 'app/utils/authentication'
import { buildDate, buildUtcDateString } from 'app/utils/dates'
import { RENT_BUTTON_CLICK_EVENT } from 'app/utils/events'
import { ItemApi } from 'appTypes/items'
import { SortingInfo } from 'appTypes/mapper'
import { AddProductToOrderOptions, OrderInfo, OrderItemInfo } from 'appTypes/orders'
import apiClient from 'helpers/clients/apiClient'
import { AppDispatch } from 'store'
import {
  addProductToOrder,
  createNewOrder,
  getOrderItemNoRequest,
  getOrderNoRequest,
  updateProductInOrder,
} from 'store/partials/cart/cartSlice'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'
import { STORAGE_KEYS } from 'utils/constants'
import { getPaymentSelectPageUrl } from 'utils/redirectUrlsBuilders'
import { getFromStorage } from 'utils/storage'

import RentProduct from '../../../RentProduct/RentProduct'
import { RentProductLoginSubmitHandler } from '../../../RentProduct/RentProductLogic/interfaces'

type Props = {
  product: ItemApi
  customerInfo: CustomerInfo
  isEditMode: boolean
  isProlonging: boolean
  order: OrderInfo | undefined
  orderItem: OrderItemInfo | undefined
  productCalendarList: ProductAvailabilityInfo
  productLocations: Location[]
  openingHours: ProductOpeningHoursResponse[]
  rentedFrom: string
  rentedTo: string
  isCleaning: boolean
  isInsurance: boolean
  locationId: number | undefined
  regionId: number | undefined
  managerId: number | undefined
  amount: number | undefined
  step: number | undefined
  isRerent: boolean
  regions: Region[]
  manager: Manager | undefined
  sortingInfo: SortingInfo | undefined
}

const RightColumn = (props: Props): JSX.Element => {
  const {
    product,
    isEditMode,
    isProlonging,
    customerInfo,
    order,
    orderItem,
    productCalendarList,
    productLocations,
    openingHours,
    rentedFrom,
    rentedTo,
    isCleaning,
    isInsurance,
    locationId,
    regionId,
    managerId,
    amount,
    step,
    isRerent,
    regions,
    manager,
    sortingInfo,
  } = props

  const router = useRouter()
  const pathname = usePathname()

  const dispatch = useDispatch<AppDispatch>()
  const { t } = useTranslation(['common'])

  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  useEffect(() => {
    const handler = () => setIsOpen(true)
    window.addEventListener(RENT_BUTTON_CLICK_EVENT, handler)

    return () => window.removeEventListener(RENT_BUTTON_CLICK_EVENT, handler)
  }, [])

  useEffect(() => {
    // TODO: Probably remove or replace at some point
    if (orderItem) {
      dispatch(getOrderItemNoRequest(orderItem))
    }

    if (order) {
      dispatch(getOrderNoRequest(order))
    }
  }, [])

  const handleClose = () => {
    setIsOpen(false)
    document.body.style.overflow = 'unset'
  }

  const onSubmit: RentProductLoginSubmitHandler = async (values, { setSubmitting }) => {
    let orderId = getFromStorage(STORAGE_KEYS.orderId)
    if (!orderId) {
      orderId = await dispatch(createNewOrder(values.managerId))
    } else {
      if (values.managerId) {
        // Order exists, if it's not for manager - create a new order instead
        const response = await apiClient.getOrderClean(orderId)
        const order = response.data

        if (!order || order.order.managerId !== values.managerId) {
          orderId = await dispatch(createNewOrder(values.managerId))
        }
      }
    }

    const rentedFrom = buildUtcDateString(values.startDate, values.pickupAt)
    const rentedTo = buildUtcDateString(values.endDate, values.returnAt)
    const rentedToDate = buildDate(values.endDate, values.returnAt)
    const orderItemId = orderItem?.orderItem?.id ?? 0

    if (isProlonging) {
      try {
        const prolongationResponse = await apiClient.createProlongation(orderItemId, {
          prolongTo: rentedToDate,
        })
        if (!prolongationResponse.success || !prolongationResponse.data) {
          if (prolongationResponse.error?.errors.some((s) => s.title === 'Item unavailable')) {
            dispatch(displaySnackbar({ message: t('common:itemUnavailable'), isError: true }))
          } else {
            dispatch(displaySnackbar({ message: t('error'), isError: true }))
          }
          return
        }

        const prolongationInfo = prolongationResponse.data

        if (prolongationInfo.confirmed) {
          router.push(`/payment/success?isProlonging=true&order_item_id=${orderItemId}`)
        } else {
          const prolongationOrderId = prolongationInfo.prolongation.orderId
          const orderItemId = prolongationInfo.prolongation.id
          const purchaseAs = customerInfo.organization ? 'organization' : 'person'

          router.push(getPaymentSelectPageUrl(prolongationOrderId, orderItemId, purchaseAs))
        }
      } catch {
        dispatch(displaySnackbar({ message: t('error'), isError: true }))
      }
    } else {
      const options: AddProductToOrderOptions = {
        orderId,
        itemId: product.id,
        rentedFrom,
        rentedTo,
        resignFromCleaning: !values.includeCleaning,
        resignFromInsurance: !values.includeInsurance,
        complementaryItems: values.complementaryItemsQuantity,
        amount: values.amount,
        locationId: values.selectedLocationId ?? 0,
        regionId: values.selectedRegionId,
        pathname: pathname ?? '',
      }

      if (isEditMode) {
        dispatch(updateProductInOrder({ ...options, orderItemId })).finally(() => {
          setSubmitting(false)
        })
      } else {
        dispatch(addProductToOrder(options)).finally(() => {
          setSubmitting(false)
        })
      }
    }
  }

  return (
    <Box
      flex="1 1 66.66%"
      marginBottom="20px"
      css={{
        '@media (max-width: 64rem)': {
          width: '100%',
          position: 'fixed',
          left: '100vw',
          top: '76px',
          transform: `translateX(${isOpen ? -100 : 0}%)`,
          zIndex: '10',
          backgroundColor: 'white',
          transition: 'transform 0.3s ease-in-out',
          '& > *': {
            paddingLeft: '20px',
            paddingRight: '20px',
          },
        },
      }}
    >
      <GoBackLink display={{ base: 'flex', lg: 'none' }} onClick={handleClose} />

      <RentProduct
        product={product}
        orderInfo={order}
        orderItemInfo={orderItem}
        customerInfo={customerInfo}
        isProlonging={isProlonging}
        isEditMode={isEditMode}
        productCalendarList={productCalendarList}
        productLocations={productLocations}
        openingHours={openingHours}
        onSubmit={onSubmit}
        rentedFrom={rentedFrom}
        rentedTo={rentedTo}
        amount={amount}
        isCleaning={isCleaning}
        isInsurance={isInsurance}
        isRerent={isRerent}
        locationId={locationId}
        managerId={managerId}
        regionId={regionId}
        step={step}
        regions={regions}
        manager={manager}
        sortingInfo={sortingInfo}
      />
    </Box>
  )
}

export default RightColumn
